import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import getUserAgentDetails from 'helpers/getUserAgentDetails';

const useInitSentry = (isRunningLocally) => {
  const [sentryInitialized, setSentryInitialized] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const userAgentDetails = getUserAgentDetails();

    if (!sentryInitialized && !isRunningLocally) {
      Sentry.init({
        dsn: 'https://1c5f985df0f013c80d77102d1fe253e6@o1258962.ingest.us.sentry.io/4506020677746688',
        integrations: [Sentry.browserTracingIntegration()],
        environment: process.env.NODE_ENV,
        tracesSampleRate: 1.0,
        initialScope: {
          tags: {
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            screen: `${window.screen.width}x${window.screen.height}`,
          },
          contexts: {
            screen: {
              width: window.screen.width,
              height: window.screen.height,
            },
            device: {
              screen_width_pixels: window.screen.width,
              screen_height_pixels: window.screen.height,
              model: userAgentDetails?.device?.model,
              name: userAgentDetails?.device?.model,
              manufacturer: userAgentDetails?.device?.vendor,
              brand: userAgentDetails?.device?.vendor,
              arch: userAgentDetails?.cpu?.architecture,
              device_type: userAgentDetails?.device?.type,
            },
            os: {
              name: userAgentDetails?.os?.name,
              version: userAgentDetails?.os?.version,
            },
          },
        },
        beforeSend(event) {
          try {
            const updatedEvent = event;
            updatedEvent.contexts = {
              ...event.contexts,
              device: {
                screen_width_pixels: window.screen.width,
                screen_height_pixels: window.screen.height,
                model: userAgentDetails?.device?.model,
                name: userAgentDetails?.device?.model,
                brand: userAgentDetails?.device?.vendor,
                arch: userAgentDetails?.cpu?.architecture,
                device_type: userAgentDetails?.device?.type,
                screen: {
                  width: window.screen.width,
                  height: window.screen.height,
                  availWidth: window.screen.availWidth,
                  availHeight: window.screen.availHeight,
                },
                os: {
                  name: userAgentDetails?.os?.name,
                  version: userAgentDetails?.os?.version,
                },
                window: {
                  innerWidth: window.innerWidth,
                  innerHeight: window.innerHeight,
                },
                userAgent: navigator.userAgent,
                language: navigator.language,
              },
            };
            return updatedEvent;
          } catch (e) {
            console.warn('Failed to attach device info to Sentry event:', e);
          }

          return event;
        },
      });

      setSentryInitialized(true);

      // Log screen resize as breadcrumbs
      const handleResize = () => {
        Sentry.setContext('screen', {
          width: window.screen.width,
          height: window.screen.height,
        });
        Sentry.setContext('device', {
          screen_width_pixels: window.screen.width,
          screen_height_pixels: window.screen.height,
          model: userAgentDetails?.device?.model,
          manufacturer: userAgentDetails?.device?.vendor,
          arch: userAgentDetails?.cpu?.architecture,
          device_type: userAgentDetails?.device?.type,
        });
        Sentry.setTag('viewport', `${window.innerWidth}x${window.innerHeight}`);
        Sentry.setTag('screen', `${window.screen.width}x${window.screen.height}`);
        Sentry.addBreadcrumb({
          category: 'viewport',
          message: `Screen resized to ${window.innerWidth}x${window.innerHeight}`,
          level: 'info',
        });
      };

      window.addEventListener('resize', handleResize);

      // Cleanup on unmount:
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    return undefined;
  }, [isRunningLocally, sentryInitialized]);
};

export default useInitSentry;
